import React, { useContext } from 'react'
import totalConsumptionData, { CountryData } from 'data/total-consumption.csv'
import { extractValue } from 'lib/extractValue'
import styles from './styles.module.scss'
import { Consumption } from 'contexts/consumption'
import { useTransition, animated as a, config } from 'react-spring'
import { getMemoisedYearlyConsumption } from 'lib/calculations'
import { max } from 'd3-array'
import { scaleLinear } from 'd3-scale'
import { ColourLegend } from 'components/colourLegend'
import { Locale } from 'contexts/locale'

export function Histogram() {
  const locale = useContext(Locale)
  const messages = require('./locales/' + locale + '.yml')
  const consumption = useContext(Consumption)
  const sex = consumption.isFemale ? 'Female' : 'Male'
  const userConsumption = getMemoisedYearlyConsumption(consumption)
  const userDataPoint: CountryData = {
    Country: 'User',
    Male: userConsumption + ' [0-0]',
    Female: userConsumption + ' [0-0]',
    'Both sexes': userConsumption + ' [0-0]',
    Year: '2019'
  }

  const allData = [].concat(totalConsumptionData, [userDataPoint])

  allData.sort((a, b) => extractValue(a[sex]) - extractValue(b[sex]))

  // After the data has been sorted, we can now just access the first item
  const maximumValue = max(allData, (d: CountryData) => extractValue(d.Male))

  const columnWidth = 5
  const gap = 3

  const maxWidth = allData.length * (columnWidth + gap)

  const scaleY = scaleLinear()
    .domain([0, maximumValue])
    .range([maximumValue, 0])
  const scaleYPercent = scaleY.copy().range([100, 0])
  const transitions = useTransition(
    allData.map((consumption, index) => ({
      ...consumption,
      width: columnWidth,
      x: index * (columnWidth + gap),
      height: extractValue(consumption[sex]) || 0,
      y: scaleY(extractValue(consumption[sex]) || 0)
    })),
    d => d.Country,
    {
      enter: ({ x, y, height }) => ({ x, y, height }),
      initial: ({ x, y, height }) => ({ x, y, height }),
      update: ({ x, y, height }) => ({ x, y, height }),
      config: config.gentle,
      unique: true,
      trail: 5
    }
  )

  return (
    <figure className={styles.histogram}>
      <figcaption className={styles.chartHeader}>
        {messages['chartHeader']}
      </figcaption>
      <ColourLegend
        parts={[
          { label: messages['labels']['switzerland'], color: '#E23C4E' },
          { label: messages['labels']['yourConsumption'], color: '#1D5F82' }
        ]}
      />

      <div className={styles.chartContainer}>
        {' '}
        <svg
          className={styles.svg}
          viewBox={`0 0 ${maxWidth} ${maximumValue}`}
          preserveAspectRatio='none'
        >
          {scaleY.ticks(5).map(value => (
            <line
              key={`tickLine-${value}`}
              y1={scaleY(value)}
              y2={scaleY(value)}
              x1={0}
              x2={maxWidth}
              className={styles.tickLine}
            />
          ))}

          {transitions.map(({ item, props, key }, index) => {
            return (
              <a.rect
                key={key}
                height={props.height}
                width={item.width}
                x={props.x}
                y={props.y}
                className={styles.column + ' ' + item.Country}
              />
            )
          })}
        </svg>
        {scaleY.ticks(5).map(tick => (
          <div
            key={tick}
            className={styles.tick}
            style={{ top: scaleYPercent(tick) + '%' }}
          >
            {tick}l
          </div>
        ))}
      </div>
    </figure>
  )
}
